import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UtensilsCrossed, Image, CalendarCheck, Clock, AlertCircle, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';

const statusLabels = { nouveau: 'Nouveau', en_cours: 'En cours', confirme: 'Confirmé', refuse: 'Refusé' };
const statusColors = { nouveau: 'bg-blue-100 text-blue-800', en_cours: 'bg-yellow-100 text-yellow-800', confirme: 'bg-green-100 text-green-800', refuse: 'bg-red-100 text-red-800' };

export default function Dashboard() {
  const { isModuleEnabled } = useAuth();
  const reservationsEnabled = isModuleEnabled('RESERVATIONS');
  const [stats, setStats] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps -- One-time init on mount; stable module-level API refs
    adminApi.getStats()
      .then(r => setStats(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Impossible de charger les statistiques'));
    adminApi.getEmailStatus()
      .then(r => setEmailStatus(r.data))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return (
    <AdminLayout title="Tableau de bord">
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="dashboard-error">
        <AlertCircle className="mx-auto text-red-500 mb-2" size={24} />
        <p className="text-sm text-red-700 font-medium">{error}</p>
      </div>
    </AdminLayout>
  );

  if (!stats) return <AdminLayout title="Tableau de bord"><div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-garden-800 border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  const cards = [
    { label: 'Plats actifs', value: stats.total_items, icon: UtensilsCrossed, color: 'bg-garden-100 text-garden-800' },
    { label: 'Catégories', value: stats.total_categories, icon: Clock, color: 'bg-cream text-wood' },
    { label: 'Photos', value: stats.total_photos, icon: Image, color: 'bg-garden-100 text-garden-800' },
    ...(reservationsEnabled ? [{ label: 'Réservations', value: stats.total_reservations, icon: CalendarCheck, color: 'bg-cream text-wood' }] : []),
  ];

  return (
    <AdminLayout title="Tableau de bord">
      <div data-testid="admin-dashboard">
        {/* Email MOCKED warning */}
        {emailStatus && !emailStatus.configured && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="email-mocked-warning">
            <Mail className="text-amber-600 mt-0.5 shrink-0" size={20} />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Les emails sont en mode TEST (MOCKED)
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Les notifications (réservations, etc.) ne sont pas réellement envoyées. 
                <Link to="/admin/parametres" className="underline font-medium ml-1" data-testid="email-config-link">
                  Configurer SMTP ou Resend dans Paramètres
                </Link>
              </p>
            </div>
          </div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Card key={c.label} className="border-garden-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${c.color}`}>
                      <Icon size={20} />
                    </div>
                    <div>
                      <p className="text-2xl font-heading font-bold text-garden-800">{c.value}</p>
                      <p className="text-xs text-charcoal/60">{c.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Pending alert */}
        {reservationsEnabled && stats.pending_reservations > 0 && (
          <div className="bg-sunny/20 border border-sunny rounded-xl p-4 mb-8 flex items-center gap-3" data-testid="pending-alert">
            <AlertCircle className="text-yellow-700" size={20} />
            <span className="text-sm font-medium text-yellow-800">
              {stats.pending_reservations} réservation(s) en attente de traitement
            </span>
          </div>
        )}

        {/* Recent reservations */}
        {reservationsEnabled && (
        <>
        <h2 className="text-lg font-heading font-semibold text-garden-800 mb-4">Dernières réservations</h2>
        <div className="bg-white rounded-xl border border-garden-200 overflow-hidden">
          {stats.recent_reservations?.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-garden-50">
                <tr>
                  <th className="text-left p-3 font-medium text-garden-800">Nom</th>
                  <th className="text-left p-3 font-medium text-garden-800 hidden sm:table-cell">Date</th>
                  <th className="text-left p-3 font-medium text-garden-800 hidden md:table-cell">Personnes</th>
                  <th className="text-left p-3 font-medium text-garden-800">Statut</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_reservations.map((r) => (
                  <tr key={r.id} className="border-t border-garden-100">
                    <td className="p-3 text-charcoal">{r.name}</td>
                    <td className="p-3 text-charcoal/70 hidden sm:table-cell">{r.date} à {r.time}</td>
                    <td className="p-3 text-charcoal/70 hidden md:table-cell">{r.guests}</td>
                    <td className="p-3">
                      <Badge className={`${statusColors[r.status]} border-0 text-xs`}>
                        {statusLabels[r.status]}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center py-8 text-charcoal/50 text-sm">Aucune réservation récente</p>
          )}
        </div>
        </>
        )}
      </div>
    </AdminLayout>
  );
}
